import { useEffect, useRef, type ReactNode } from 'react';
import { AlertTriangle, HelpCircle, Loader2, Trash2, X } from 'lucide-react';

/**
 * Visual + tonal variant for the confirm action.
 *
 *   - `'default'` — neutral, emerald confirm button. Use for benign actions
 *     ("Publish", "Save now").
 *   - `'danger'`  — red confirm button + warning icon. Use for destructive
 *     actions ("Delete", "Remove", "Purge").
 */
export type ConfirmVariant = 'default' | 'danger';

export interface ConfirmModalProps {
  isOpen: boolean;
  /** Bold heading shown at the top of the dialog. */
  title: string;
  /**
   * Body copy. Plain string is rendered as a single paragraph; pass a
   * React node for richer content (lists, inline `<code>`, etc.).
   */
  description?: ReactNode;
  /** Confirm-button label. Defaults to `'Confirm'`. */
  confirmLabel?: string;
  /** Cancel-button label. Defaults to `'Cancel'`. */
  cancelLabel?: string;
  /** When true, the confirm button shows an icon only (label stays for screen readers). */
  iconOnlyConfirm?: boolean;
  variant?: ConfirmVariant;
  /**
   * When true, both buttons are disabled and a spinner is shown on the
   * confirm button. Use while the parent's `onConfirm` is in flight.
   */
  busy?: boolean;
  /**
   * Fired when the user accepts. May be async — the parent is responsible
   * for closing the modal when the work finishes (typically by flipping
   * `isOpen` back to `false`).
   */
  onConfirm: () => void | Promise<void>;
  /**
   * Fired when the user cancels (X button, backdrop click, Escape key, or
   * the explicit Cancel button).
   */
  onClose: () => void;
}

/**
 * Shared yes/no confirmation modal. Replaces ad-hoc `window.confirm()` calls
 * with a styled, themable dialog that supports a destructive variant.
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   ...
 *   <ConfirmModal
 *     isOpen={open}
 *     title="Remove template?"
 *     description="The newsletter will fall back to raw HTML on send."
 *     confirmLabel="Remove"
 *     variant="danger"
 *     onConfirm={() => { doRemove(); setOpen(false); }}
 *     onClose={() => setOpen(false)}
 *   />
 */
export default function ConfirmModal({
  isOpen,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  iconOnlyConfirm = false,
  variant = 'default',
  busy = false,
  onConfirm,
  onClose,
}: ConfirmModalProps) {
  const confirmBtnRef = useRef<HTMLButtonElement | null>(null);

  // Keyboard handling: Escape cancels, Enter confirms (when not already
  // focused on Cancel, so the user can still arrow over and confirm with
  // Space if they prefer).
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (!busy) onClose();
      } else if (e.key === 'Enter') {
        // Only auto-confirm when focus isn't sitting on a different button or
        // textbox; this keeps Enter from firing twice if a parent input was
        // focused right before opening.
        const active = document.activeElement;
        const isOtherButton =
          active instanceof HTMLButtonElement && active !== confirmBtnRef.current;
        if (isOtherButton) return;
        e.preventDefault();
        if (!busy) void onConfirm();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, busy, onClose, onConfirm]);

  // Autofocus the confirm button on open so Enter Just Works.
  useEffect(() => {
    if (!isOpen) return;
    const t = window.setTimeout(() => confirmBtnRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [isOpen]);

  if (!isOpen) return null;

  const isDanger = variant === 'danger';
  const Icon = isDanger ? AlertTriangle : HelpCircle;
  const iconCls = isDanger
    ? 'text-red-500'
    : 'text-emerald-500';
  const confirmCls = isDanger
    ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
    : 'bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500';

  return (
    <div
      className="fixed inset-0 z-[80] flex items-stretch sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      onClick={() => {
        if (!busy) onClose();
      }}
      role="presentation"
    >
      <div
        className="bg-white dark:bg-gray-800 shadow-xl w-full max-w-md flex flex-col min-h-0 h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(90vh,900px)] sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
      >
        <div className="flex items-start gap-3 px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${iconCls}`} aria-hidden="true" />
          <h2
            id="confirm-modal-title"
            className="flex-1 text-base font-semibold text-gray-900 dark:text-white"
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="p-1 -m-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-40"
            aria-label="Close"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {description !== undefined && description !== null ? (
          <div className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300 leading-relaxed overflow-y-auto flex-1 min-h-0">
            {typeof description === 'string' ? (
              <p>{description}</p>
            ) : (
              description
            )}
          </div>
        ) : (
          <div className="px-6 py-2 flex-1 min-h-0" />
        )}

        <div className="shrink-0 flex justify-end gap-2 px-6 py-3 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmBtnRef}
            type="button"
            onClick={() => {
              if (!busy) void onConfirm();
            }}
            disabled={busy}
            aria-label={iconOnlyConfirm ? confirmLabel : undefined}
            title={iconOnlyConfirm ? confirmLabel : undefined}
            className={`inline-flex items-center justify-center gap-1.5 ${
              iconOnlyConfirm ? 'p-2' : 'px-3 py-1.5'
            } text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-offset-white dark:focus:ring-offset-gray-800 disabled:opacity-60 ${confirmCls}`}
          >
            {busy ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : iconOnlyConfirm ? (
              <Trash2 className="w-3.5 h-3.5" />
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
